import { useCallback, useEffect } from 'react';
import { apiErrorMessage } from '../api/errors';
import { vaultApi } from '../api/vaultApi';
import { useAdminBonusesStore } from '../stores/adminBonusesStore';
import { useUiStore } from '../stores/uiStore';
import type { BonusPack } from '../types/domain';

export function useAdminBonuses() {
  const bonuses = useAdminBonusesStore((s) => s.bonuses);
  const load = useAdminBonusesStore((s) => s.load);
  const loadingIds = useAdminBonusesStore((s) => s.loadingIds);
  const loadBonuses = useAdminBonusesStore((s) => s.loadBonuses);
  const createBonusRaw = useAdminBonusesStore((s) => s.createBonus);
  const updateBonusRaw = useAdminBonusesStore((s) => s.updateBonus);
  const deleteBonusRaw = useAdminBonusesStore((s) => s.deleteBonus);
  const pending = load.status === 'loading';
  const showToast = useUiStore((s) => s.showToast);
  const getBonusClaimRecipients = useCallback((id: string) => vaultApi.getAdminBonusClaims(id), []);

  useEffect(() => {
    if (load.status === 'idle') void loadBonuses();
  }, [load.status, loadBonuses]);

  return {
    bonuses,
    load,
    loadingIds,
    loadBonuses,
    getBonusClaimRecipients,
    pending,
    async previewBonusEmail(bonus: BonusPack): Promise<string> {
      const preview = await vaultApi.previewAdminBonusEmail(bonus);
      return preview.html;
    },
    async createBonus(bonus: BonusPack): Promise<void> {
      try {
        await createBonusRaw(bonus);
        showToast('Bonus pack created');
      } catch (err) {
        showToast(apiErrorMessage(err, { preserveMessage: false }));
      }
    },
    async updateBonus(id: string, patch: Partial<BonusPack>): Promise<void> {
      try {
        await updateBonusRaw(id, patch);
        showToast('Bonus pack updated');
      } catch (err) {
        showToast(apiErrorMessage(err, { preserveMessage: false }));
      }
    },
    async deleteBonus(id: string): Promise<void> {
      try {
        await deleteBonusRaw(id);
        showToast('Bonus pack deleted');
      } catch (err) {
        showToast(apiErrorMessage(err, { preserveMessage: false }));
      }
    },
    isPending(id: string): boolean {
      return loadingIds.includes(id);
    },
  };
}
